import { GenieAlloText, GenieLabText, GeniePmsText } from 'components/technical/icons';
import { getBaseEnvType, getEnvType, getRedirectUrls } from 'envConfig';
import { createContext, type ReactElement, useContext, useMemo, type ReactNode } from 'react';
import { useLocalStorage } from 'react-use';
import { useFinalColorScheme } from 'technical/useFinalColorScheme';

export type LocalApp = 'pms' | 'portfolioLab';
export type App = LocalApp | 'marketplace';

const PmsLogo = ({ width }: { width: string }): ReactElement => {
  const colorScheme = useFinalColorScheme();
  return (
    <GeniePmsText
      sx={{
        width,
        height: 'auto',
        fill: colorScheme === 'light' ? '#303136' : 'white', // logo colors for white and dark variants
      }}
    />
  );
};

const LabLogo = ({ width }: { width: string }): ReactElement => {
  const colorScheme = useFinalColorScheme();
  return (
    <GenieLabText
      sx={{
        width,
        height: 'auto',
        fill: colorScheme === 'light' ? '#303136' : 'white', // logo colors for white and dark variants
      }}
    />
  );
};

const AlloLogo = ({ width }: { width: string }): ReactElement => {
  const colorScheme = useFinalColorScheme();
  return (
    <GenieAlloText
      sx={{
        width,
        height: 'auto',
        fill: colorScheme === 'light' ? '#303136' : 'white', // logo colors for white and dark variants
      }}
    />
  );
};

export interface LocalApplicationConfig {
  app: LocalApp;
  displayName?: string;
  Logo: ({ width }: { width: string }) => ReactNode;
  changeApplication: (app: App) => {
    href?: string;
    onClick?: () => void;
  };
  cognitoGroups: string[];
  shortName: string;
  local: boolean;
}

export const appConfigs: Record<App, Omit<LocalApplicationConfig, 'changeApplication' | 'app'>> = {
  pms: {
    displayName: 'Genie AI PMS',
    Logo: PmsLogo,
    cognitoGroups: ['pms'],
    shortName: 'PMS',
    local: true,
  },
  portfolioLab: {
    displayName: 'Genie AI Lab',
    Logo: LabLogo,
    cognitoGroups: ['lab'],
    shortName: 'Lab',
    local: true,
  },
  marketplace: {
    cognitoGroups: ['manager', 'allocator'],
    shortName: 'Allo',
    Logo: AlloLogo,
    local: false,
  },
};

export const localApps = Object.entries(appConfigs)
  .filter(([_, config]) => config.local)
  .map(([app]) => app);

export const appEnvUrlConfigs: Record<
  'dev' | 'prod',
  Record<App, { domain: string; redirectSignInUrl: string; autoSignInPath: string }>
> = {
  dev: {
    pms: { domain: 'app.genieai.fr', redirectSignInUrl: 'https://app.genieai.fr', autoSignInPath: '/signin' },
    portfolioLab: {
      domain: 'lab.genieai.fr',
      redirectSignInUrl: 'https://lab.genieai.fr',
      autoSignInPath: '/signin',
    },
    marketplace: {
      domain: 'allo.genieai.fr',
      redirectSignInUrl: 'https://allo.genieai.fr/auth/callback',
      autoSignInPath: '/',
    },
  },
  prod: {
    pms: { domain: 'app.genieai.tech', redirectSignInUrl: 'https://app.genieai.tech', autoSignInPath: '/signin' },
    portfolioLab: {
      domain: 'lab.genieai.tech',
      redirectSignInUrl: 'https://lab.genieai.tech',
      autoSignInPath: '/signin',
    },
    marketplace: {
      domain: 'allo.genieai.tech',
      redirectSignInUrl: 'https://allo.genieai.tech/auth/callback',
      autoSignInPath: '/',
    },
  },
};

const ApplicationContext = createContext<LocalApplicationConfig>({
  app: 'pms',
  displayName: 'Genie PMS',
  Logo: PmsLogo,
  changeApplication: () => {
    throw new Error('changeApplication is not implemented');
  },
  cognitoGroups: [],
  shortName: '',
  local: false,
});

const resolveAppBasedOnDomain = (): LocalApp => {
  const domain = window.location.hostname;
  for (const env of ['dev', 'prod'] as const) {
    const envConfig = appEnvUrlConfigs[env];
    for (const app of localApps) {
      if (domain === envConfig[app as App].domain) {
        return app as LocalApp;
      }
    }
  }

  throw new Error('Unknown domain');
};

export const ApplicationProvider = ({ children }: { children: ReactNode }) => {
  const [application, setApplication] = useLocalStorage<LocalApp>('genie-app', 'pms', {
    raw: true,
  });
  const isLocalhost = getEnvType().includes('localhost');
  const app = isLocalhost ? (application ?? 'pms') : resolveAppBasedOnDomain();

  const appConfig = useMemo(() => {
    return {
      ...appConfigs[app],
      app,
      changeApplication: (app: App) => {
        if (isLocalhost && localApps.includes(app as LocalApp)) {
          return {
            onClick: () => {
              setApplication(app as LocalApp);
            },
          };
        }

        const domain = appEnvUrlConfigs[getBaseEnvType()][app];
        if (!domain) {
          throw new Error(`Domain not found for app ${app}`);
        }

        const redirectUrls = getRedirectUrls(getEnvType(), app);
        return {
          href: redirectUrls.autoSignIn,
        };
      },
    };
  }, [app, isLocalhost, setApplication]);

  return <ApplicationContext.Provider value={appConfig}>{children}</ApplicationContext.Provider>;
};

export const useApplication = (): LocalApplicationConfig => {
  return useContext(ApplicationContext);
};
